import React from 'react';

import { DownOutlined, UpOutlined } from '@ant-design/icons/lib';
import { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';

import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { useAppSelector } from 'store/hook';
import { convertAreaByUnitSystem, convertRatePerAreaByUnitSystem, getAreaUnitLabel } from 'utils/helpers/units';

import { COMMUNITIES_INDENT_SIZE, COMMUNITIES_PAGE_SIZE, COMMUNITIES_TABLE_FIRST_COLUMN_TITLE } from './constants';
import OverviewCommunitiesInfo from './OverviewCommunitiesInfo';
import OverviewCommunitiesRatingCell from './OverviewCommunitiesRatingCell';
import OverviewCommunitiesInfoValueCell from './OverviewCommunitiesValueCell';
import { StyledTable } from '../../common/Table/StyledTable';

const FirstColumnHeader = styled.span`
  padding-left: 15px;
`;

const ExpandIconContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 10px;
`;

interface Props {
  type: number;
  communities: any[];
}

const OverviewCommunitiesTablePreview = ({ type, communities }: Props) => {
  const selectedCommunity = useAppSelector((state) => state.global.selectedCommunity);
  const hasUnitSystem = (community?: any) => Boolean(community?.unit_system || community?.preferences?.unit_system);
  const firstCommunityWithUnitSource =
    communities.find((community) => hasUnitSystem(community)) ||
    communities.flatMap((community) => community?.children || []).find((community) => hasUnitSystem(community));
  const unitSource = selectedCommunity || firstCommunityWithUnitSource;
  const areaUnitLabel = getAreaUnitLabel(unitSource).toUpperCase();
  const formatAreaValue = (value: any) =>
    typeof value === 'number' ? convertAreaByUnitSystem(value, unitSource) : value;
  const formatRatePerArea = (value: any) =>
    typeof value === 'number' ? convertRatePerAreaByUnitSystem(value, unitSource) : value;

  const columns: ColumnsType<any> = [
    {
      title: <FirstColumnHeader>{COMMUNITIES_TABLE_FIRST_COLUMN_TITLE[type]}</FirstColumnHeader>,
      dataIndex: 'id',
      width: 400,
      key: 'id',
      render: (value, record: any) => {
        return <OverviewCommunitiesInfo record={record} />;
      },
    },
    {
      title: 'MARKET RATE AVG.',
      dataIndex: 'avg_base_rent',
      key: 'avg_base_rent',
      width: 200,
      render: (value, record: any) => {
        return (
          <OverviewCommunitiesInfoValueCell value={value} marketValue={record.market_avg_base_rent} prefix={'$'} />
        );
      },
    },
    {
      title: `${areaUnitLabel} AVG.`,
      dataIndex: 'avg_square_footage',
      key: 'avg_square_footage',
      width: 200,
      render: (value, record: any) => (
        <OverviewCommunitiesInfoValueCell
          value={formatAreaValue(value)}
          marketValue={formatAreaValue(record.market_avg_square_footage)}
        />
      ),
    },
    {
      title: `RATE/${areaUnitLabel} AVG.`,
      dataIndex: 'avg_price_per_square_foot',
      key: 'avg_price_per_square_foot',
      width: 200,
      render: (value, record: any) => (
        <OverviewCommunitiesInfoValueCell
          value={formatRatePerArea(value)}
          marketValue={formatRatePerArea(record.market_avg_price_per_square_foot)}
          prefix={'$'}
        />
      ),
    },
    {
      title: 'RATING AVG.',
      dataIndex: 'ratings_average',
      key: 'ratings_average',
      width: 200,
      render: (value, record: any) => {
        const isCompetitor = record?.company_relation_type == COMPANY_COMMUNITY_RELATION_TYPE.COMPETITOR;
        const marketValue = isCompetitor ? 0 : record?.market_ratings_average;
        const marketTotal = isCompetitor ? 0 : record?.market_ratings_count;

        return (
          <OverviewCommunitiesRatingCell
            value={record?.ratings_average}
            total={record?.ratings_count}
            marketValue={marketValue}
            marketTotal={marketTotal}
          />
        );
      },
    },
  ];

  return (
    <StyledTable
      columns={columns}
      size={'small'}
      dataSource={communities}
      bordered={false}
      rowKey={(record: any) => record.id}
      expandable={{
        indentSize: COMMUNITIES_INDENT_SIZE,
        expandIcon: ({ expanded, onExpand, record }: { expanded: boolean; onExpand: any; record: any }) => (
          <ExpandIconContainer>
            {record?.company_relation_type === COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY &&
              record.children?.length > 0 && (
                <>
                  {expanded ? (
                    <UpOutlined onClick={(e) => onExpand(record, e)} />
                  ) : (
                    <DownOutlined onClick={(e) => onExpand(record, e)} />
                  )}
                </>
              )}
          </ExpandIconContainer>
        ),
      }}
      pagination={{
        size: 'small',
        position: ['bottomLeft'],
        showSizeChanger: false,
        pageSize: COMMUNITIES_PAGE_SIZE,
        showTotal: (total, range) => {
          const displayedItems = range[1] - range[0] + 1;
          return (
            <span>
              Showing {displayedItems} of {total} records
            </span>
          );
        },
      }}
    />
  );
};

export default OverviewCommunitiesTablePreview;
